import {
  ICustomNumberFormatConfig,
  IFormatterDataModel,
  StandardFormatterId,
} from '@foresightdata/web-common-core';
import { FMT } from '@foresightdata/web-common-formatter-service';

type MoneyModel = IFormatterDataModel<ICustomNumberFormatConfig>;
type Formatter = IFormatterDataModel<unknown> | string;

interface IMoneyField {
  formatter?: Formatter;
  rendererMeta?: unknown;
}

function foreignCode(code?: string | null): string | undefined {
  const currency = code?.toUpperCase();
  return currency && currency !== 'USD' ? currency : undefined;
}

/** Formatters whose output carries a currency symbol: the registered currency* / usd* ids and any model styled as currency. */
export function isMoneyFormatter(formatter?: Formatter | null): boolean {
  if (!formatter) {
    return false;
  }
  if (typeof formatter === 'string') {
    return formatter.startsWith('currency') || formatter.startsWith('usd');
  }
  const config = formatter.config as ICustomNumberFormatConfig | undefined;
  return (
    config?.style === 'currency' || formatter.id.startsWith('currency') || formatter.id.startsWith('usd')
  );
}

/**
 * The id of a money formatter pinned to `code`. The app-wide currency toggle rewrites every
 * registered id that starts with `currency` or `usd` (`updateCurrencyFormatters`), so pinned
 * models live under the `financials-currency:` namespace the financials grid already uses.
 */
export function pinnedCurrencyId(code: string, baseId: string): string {
  return baseId === 'currency' ? `financials-currency:${code}` : `financials-currency:${code}:${baseId}`;
}

/**
 * The same money formatter aimed at `code`, under a pinned id the app-wide currency toggle never
 * rewrites. USD or no code returns the model as it is.
 */
export function modelInCurrency<T extends IFormatterDataModel<unknown>>(model: T, code?: string | null): T {
  const currency = foreignCode(code);
  if (!currency) {
    return model;
  }
  const registered = FMT.get().getFormatterModel(model.id as StandardFormatterId) as MoneyModel | undefined;
  const config = { ...registered?.config, ...(model.config as ICustomNumberFormatConfig | undefined) };
  if (config.currency === currency) {
    return model;
  }
  return { ...model, id: pinnedCurrencyId(currency, model.id), config: { ...config, currency } };
}

/** A registered formatter id aimed at `code`; non-money ids and USD pass through unchanged. */
export function formatterInCurrency(
  formatterId: StandardFormatterId,
  code?: string | null
): StandardFormatterId | MoneyModel {
  const currency = foreignCode(code);
  if (!currency || !isMoneyFormatter(formatterId)) {
    return formatterId;
  }
  const model = FMT.get().getFormatterModel(formatterId) as MoneyModel | undefined;
  return model ? modelInCurrency(model, currency) : formatterId;
}

/** A field whose money formatter, and the currency its input shows, follow `code`. */
export function fieldInCurrency<T extends IMoneyField>(field: T, code?: string | null): T {
  const currency = foreignCode(code);
  if (!currency || !isMoneyFormatter(field.formatter)) {
    return field;
  }
  const formatter =
    typeof field.formatter === 'string'
      ? formatterInCurrency(field.formatter as StandardFormatterId, currency)
      : modelInCurrency(field.formatter as IFormatterDataModel<unknown>, currency);
  return {
    ...field,
    formatter,
    rendererMeta: {
      ...(field.rendererMeta as Record<string, unknown> | undefined),
      defaultCurrency: currency,
    },
  };
}
